import React, { useState, useRef } from "react";
import ReactPlayer from "react-player";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import MaterialIcon from "../MaterialIcon/MaterialIcon";
import useStore from "../../store.js";
import "./AudioPlayer.css";

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function AudioPlayer({ url, transcript }) {
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [seeking, setSeeking] = useState(false);
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const playerRef = useRef(null);
  const progressRef = useRef(null);
  const getLocalized = useStore((state) => state.getLocalized);

  const handleProgress = (state) => {
    if (!seeking) setPlayed(state.played);
  };

  const handleSeekStart = (e) => {
    setSeeking(true);
    updateSeek(e);
  };

  const handleSeekMove = (e) => {
    if (!seeking) return;
    updateSeek(e);
  };

  const handleSeekEnd = () => {
    setSeeking(false);
  };

  const updateSeek = (e) => {
    const bar = progressRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const fraction = Math.min(
      Math.max((clientX - rect.left) / rect.width, 0),
      1,
    );
    setPlayed(fraction);
    playerRef.current?.seekTo(fraction, "fraction");
  };

  return (
    <div className="audioPlayer">
      <ReactPlayer
        ref={playerRef}
        url={url}
        playing={playing}
        onProgress={handleProgress}
        onDuration={setDuration}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setPlayed(0);
          playerRef.current?.seekTo(0);
        }}
        controls={false}
        width="0"
        height="0"
        config={{ file: { attributes: { controlsList: "nodownload" } } }}
        onContextMenu={(e) => e.preventDefault()}
      />

      <MaterialIcon
        name={playing ? "pause_circle" : "play_circle"}
        className="audioIcon"
        onClick={() => setPlaying(!playing)}
      />
      <div className={`audioHint ${playing ? "hidden" : ""}`}>
        {getLocalized("texts", "AudioHint")}
      </div>

      <div className="audioBottom">
        <div className="audioControls">
          <span className="audioTime">{formatTime(played * duration)}</span>
          <div
            className="audioProgressBar"
            ref={progressRef}
            onMouseDown={handleSeekStart}
            onMouseMove={handleSeekMove}
            onMouseUp={handleSeekEnd}
            onMouseLeave={handleSeekEnd}
            onTouchStart={handleSeekStart}
            onTouchMove={handleSeekMove}
            onTouchEnd={handleSeekEnd}
          >
            <div
              className="audioProgressFill"
              style={{ width: `${played * 100}%` }}
            />
            <div
              className="audioProgressThumb"
              style={{ left: `${played * 100}%` }}
            />
          </div>
          <span className="audioTime">{formatTime(duration)}</span>
        </div>

        {transcript && (
          <div className="audioTextContainer">
            <button
              className={`audioTextToggle${transcriptExpanded ? " expanded" : ""}`}
              onClick={() => setTranscriptExpanded(!transcriptExpanded)}
              type="button"
            >
              {transcriptExpanded
                ? getLocalized("texts", "TextAusblenden")
                : getLocalized("texts", "TextEinblenden")}
            </button>
            <div
              className={`audioTextContent${transcriptExpanded ? " expanded" : ""}`}
            >
              <BlocksRenderer content={transcript} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AudioPlayer;
